import { computed, watch, onMounted, ref } from 'vue';
import { useForm, useField } from 'vee-validate';
import { useStore } from 'vuex';
import * as yup from 'yup';
import { format } from 'date-fns';
import { useRollingFundingInquiries } from '@/composables/useRollingFundingInquiries';
import type {
  RollingFundingInquiry,
  CreateRollingFundingInquiryPayload,
} from '@/types/funding-inquiry.types';
import {
  CAPITAL_USAGE_MAPPING,
  CAPITAL_USAGE_REVERSE_MAPPING,
  FundingInquiryType,
} from '@/types/funding-inquiry.types';
import { useToast } from '@/composables/useToast';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { formatMoney } from '@/utils/currency';

// ============================================================================
// Types
// ============================================================================
export const FUNDING_NEED_TYPE = {
  RECURRING: 'recurring',
  ONE_TIME: 'one-time',
} as const;

export type FundingNeedType =
  (typeof FUNDING_NEED_TYPE)[keyof typeof FUNDING_NEED_TYPE];

interface FundingNeedsFormData {
  capitalAmount: number;
  fundingType: FundingNeedType | null;
  recurringNeeds: {
    month1Amount: number | null;
    month2Amount: number | null;
    month3Amount: number | null;
  };
  oneTimeNeedDate: Date | null;
  capitalUsage: string[];
}

const defaultDates = (() => {
  const today = new Date();
  const currentDay = today.getDate();
  const daysInMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
  ).getDate();
  const daysRemainingInMonth = daysInMonth - currentDay;

  // If there are 7 or fewer days remaining in the month, start with next month
  const startMonthOffset = daysRemainingInMonth <= 7 ? 1 : 0;

  const month1 = new Date(
    today.getFullYear(),
    today.getMonth() + startMonthOffset,
    1,
  );
  const month2 = new Date(
    today.getFullYear(),
    today.getMonth() + startMonthOffset + 1,
    1,
  );
  const month3 = new Date(
    today.getFullYear(),
    today.getMonth() + startMonthOffset + 2,
    1,
  );
  return { month1, month2, month3 };
})();

const capitalUsageOptions = Object.values(CAPITAL_USAGE_MAPPING);

// ============================================================================
// Constants
// ============================================================================
export const MIN_FUNDING_AMOUNT = 200000;
export const MAX_FUNDING_AMOUNT = 3000000;

export const MIN_FUNDING_AMOUNT_FORMATTED = formatMoney(
  (MIN_FUNDING_AMOUNT * 100).toString(),
  'USD',
  null,
  true,
);

export const MAX_FUNDING_AMOUNT_FORMATTED = formatMoney(
  (MAX_FUNDING_AMOUNT * 100).toString(),
  'USD',
  null,
  true,
);

// ============================================================================
// Composable
// ============================================================================
export function useFundingNeedsForm(onSubmitSuccess?: () => void) {
  // ============================================================================
  // Validation Schema
  // ============================================================================
  const schema = yup.object({
    capitalAmount: yup
      .number()
      .required('Capital amount is required')
      .min(
        MIN_FUNDING_AMOUNT,
        `Minimum amount is ${MIN_FUNDING_AMOUNT_FORMATTED}`,
      )
      .max(
        MAX_FUNDING_AMOUNT,
        `Maximum amount is ${MAX_FUNDING_AMOUNT_FORMATTED}`,
      ),
    fundingType: yup
      .string()
      .nullable()
      .required('Please select if you have a one-time or recurring need'),
    recurringNeeds: yup.object({
      month1Amount: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        ),
      month2Amount: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        ),
      month3Amount: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        ),
    }),
    oneTimeNeedDate: yup.date().nullable(),
    capitalUsage: yup.array().of(yup.string()),
  });

  // ============================================================================
  // Composables & Dependencies
  // ============================================================================
  const { latestValidRollingFundingInquiry } = useRollingFundingInquiries();
  const store = useStore();
  const toast = useToast();

  // ============================================================================
  // Helper Methods (defined early for use in form setup)
  // ============================================================================
  /**
   * Maps API funding inquiry data to form data structure
   */
  const mapInquiryToFormData = (
    inquiry: RollingFundingInquiry,
  ): Partial<FundingNeedsFormData> => {
    if (!inquiry) return {};

    const formData: Partial<FundingNeedsFormData> = {
      capitalAmount: inquiry.fundingAmount,
      fundingType: inquiry.isOngoingNeed
        ? FUNDING_NEED_TYPE.RECURRING
        : FUNDING_NEED_TYPE.ONE_TIME,
    };

    // Map capital usage from API values to form display values

    formData.capitalUsage = inquiry.capitalUsage
      ?.map((usage) => CAPITAL_USAGE_MAPPING[usage])
      .filter(Boolean); // Remove any undefined values

    // Handle one-time need date
    if (!inquiry.isOngoingNeed && inquiry.oneTimeNeedDate) {
      formData.oneTimeNeedDate = new Date(inquiry.oneTimeNeedDate);
    }

    // Handle recurring needs (capital needs over time)
    if (inquiry.isOngoingNeed && inquiry.capitalNeeds?.length) {
      const recurringNeeds: FundingNeedsFormData['recurringNeeds'] = {
        month1Amount: null,
        month2Amount: null,
        month3Amount: null,
      };

      inquiry.capitalNeeds.slice(0, 3).forEach((need, index) => {
        const monthNumber = index + 1;
        const monthKey =
          `month${monthNumber}Amount` as keyof typeof recurringNeeds;
        recurringNeeds[monthKey] = need.amount;
      });

      formData.recurringNeeds = recurringNeeds;
    }

    return formData;
  };

  // ============================================================================
  // Form Setup
  // ============================================================================
  const getDefaultInitialValues = (): FundingNeedsFormData => ({
    capitalAmount: 300000,
    fundingType: null,
    recurringNeeds: {
      month1Amount: null,
      month2Amount: null,
      month3Amount: null,
    },
    oneTimeNeedDate: null,
    capitalUsage: [],
  });

  /**
   * Get initial form values, using latestInquiry data if available, otherwise defaults
   */
  const getInitialValues = (): FundingNeedsFormData => {
    const defaults = getDefaultInitialValues();
    const latestInquiry = latestValidRollingFundingInquiry.value;

    if (!latestInquiry) {
      return defaults;
    }

    const inquiryData = mapInquiryToFormData(latestInquiry);

    return {
      capitalAmount: inquiryData.capitalAmount ?? defaults.capitalAmount,
      fundingType: inquiryData.fundingType ?? defaults.fundingType,
      recurringNeeds: {
        month1Amount:
          inquiryData.recurringNeeds?.month1Amount ??
          defaults.recurringNeeds.month1Amount,
        month2Amount:
          inquiryData.recurringNeeds?.month2Amount ??
          defaults.recurringNeeds.month2Amount,
        month3Amount:
          inquiryData.recurringNeeds?.month3Amount ??
          defaults.recurringNeeds.month3Amount,
      },
      oneTimeNeedDate: inquiryData.oneTimeNeedDate ?? defaults.oneTimeNeedDate,
      capitalUsage: inquiryData.capitalUsage ?? defaults.capitalUsage,
    };
  };

  const { validate, values, meta } = useForm<FundingNeedsFormData>({
    validationSchema: schema,
    initialValues: getInitialValues(),
  });

  // ============================================================================
  // Form Fields (Data)
  // ============================================================================
  const { value: capitalAmount } = useField<number>('capitalAmount');
  const { value: fundingType, errorMessage: fundingTypeError } =
    useField<FundingNeedType>('fundingType');
  const { value: recurringNeeds } =
    useField<FundingNeedsFormData['recurringNeeds']>('recurringNeeds');
  const { value: oneTimeNeedDate } = useField<Date>('oneTimeNeedDate');
  const { value: capitalUsage } = useField<string[]>('capitalUsage');

  // ============================================================================
  // Computed Properties
  // ============================================================================
  const createFundingInquiryRequest = computed(() =>
    store.getters.requestStatus('CREATE_FUNDING_INQUIRY'),
  );

  const formattedCapitalAmount = computed(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(capitalAmount.value);
  });

  /**
   * Track if the form is valid (all required fields filled correctly)
   */
  const isFormValid = computed(() => {
    // Check if required fields are filled
    const hasCapitalAmount =
      values.capitalAmount >= MIN_FUNDING_AMOUNT &&
      values.capitalAmount <= MAX_FUNDING_AMOUNT;
    const hasFundingType = values.fundingType !== null;

    return meta.value.valid && hasCapitalAmount && hasFundingType;
  });

  /**
   * Compare current form values with the original inquiry to detect changes
   */
  const originalInquiryData = ref<Partial<FundingNeedsFormData> | null>(null);

  const hasFormChanged = computed(() => {
    if (!originalInquiryData.value) {
      // No original data means this is a new submission, always considered "changed"
      return true;
    }

    const original = originalInquiryData.value;

    // Compare capital amount
    if (values.capitalAmount !== original.capitalAmount) {
      return true;
    }

    // Compare funding type
    if (values.fundingType !== original.fundingType) {
      return true;
    }

    // Compare one-time need date
    if (values.fundingType === FUNDING_NEED_TYPE.ONE_TIME) {
      const originalDate = original.oneTimeNeedDate?.getTime();
      const currentDate = values.oneTimeNeedDate?.getTime();
      if (originalDate !== currentDate) {
        return true;
      }
    }

    // Compare recurring needs
    if (values.fundingType === FUNDING_NEED_TYPE.RECURRING) {
      const originalRecurring = original.recurringNeeds || {
        month1Amount: null,
        month2Amount: null,
        month3Amount: null,
      };
      if (
        values.recurringNeeds.month1Amount !== originalRecurring.month1Amount ||
        values.recurringNeeds.month2Amount !== originalRecurring.month2Amount ||
        values.recurringNeeds.month3Amount !== originalRecurring.month3Amount
      ) {
        return true;
      }
    }

    // Compare capital usage arrays
    const originalUsage = original.capitalUsage || [];
    const currentUsage = values.capitalUsage || [];
    if (originalUsage.length !== currentUsage.length) {
      return true;
    }
    const sortedOriginal = [...originalUsage].sort();
    const sortedCurrent = [...currentUsage].sort();
    if (sortedOriginal.some((val, idx) => val !== sortedCurrent[idx])) {
      return true;
    }

    return false;
  });

  // ============================================================================
  // Watchers
  // ============================================================================
  /**
   * Initialize recurring needs when funding type changes to recurring
   */
  watch(fundingType, (newType) => {
    if (
      newType === FUNDING_NEED_TYPE.RECURRING &&
      recurringNeeds.value.month1Amount == null
    ) {
      recurringNeeds.value = {
        month1Amount: null,
        month2Amount: null,
        month3Amount: null,
      };
    }
  });

  // ============================================================================
  // Methods
  // ============================================================================
  const formatDateForDisplay = (date: Date) => {
    return format(date, 'MMMM yyyy');
  };

  const getApiPayload = () => {
    const payload: CreateRollingFundingInquiryPayload = {
      fundingAmount: values.capitalAmount,
      isOngoingNeed: values.fundingType === FUNDING_NEED_TYPE.RECURRING,
    };

    // Map capital usage from form display values back to API values
    if (values.capitalUsage?.length) {
      payload.capitalUsage = values.capitalUsage
        .map((usage) => CAPITAL_USAGE_REVERSE_MAPPING[usage])
        .filter(Boolean);
    }

    // Handle recurring needs
    if (
      values.fundingType === FUNDING_NEED_TYPE.RECURRING &&
      values.recurringNeeds
    ) {
      const capitalNeeds = [
        {
          date: defaultDates.month1,
          amount: values.recurringNeeds.month1Amount || 0,
        },
        {
          date: defaultDates.month2,
          amount: values.recurringNeeds.month2Amount || 0,
        },
        {
          date: defaultDates.month3,
          amount: values.recurringNeeds.month3Amount || 0,
        },
      ].map((need) => ({
        date: format(need.date, 'yyyy-MM-dd'),
        amount: need.amount,
      }));

      payload.capitalNeeds = capitalNeeds;
    }

    // Handle one-time need date
    if (
      values.fundingType === FUNDING_NEED_TYPE.ONE_TIME &&
      values.oneTimeNeedDate
    ) {
      payload.oneTimeNeedDate = format(values.oneTimeNeedDate, 'yyyy-MM-dd');
    }

    return payload;
  };

  const submit = async () => {
    const { valid } = await validate();

    if (!valid) {
      return;
    }

    const formPayload = getApiPayload();

    const requestBody = {
      type: FundingInquiryType.ROLLING_FUNDING,
      payload: formPayload,
    };

    await store.dispatchApiAction('CREATE_FUNDING_INQUIRY', requestBody);

    if (createFundingInquiryRequest.value.isError) {
      toast.error(
        'Please try again or contact support if the issue persists.',
        {
          summary: 'Something went wrong',
        },
      );
      return;
    }

    if (createFundingInquiryRequest.value.isSuccess) {
      analytics.track(SEGMENT_EVENTS.if_rolling_funding_inquiry_submitted, {
        funding_amount: formPayload.fundingAmount,
        is_ongoing_need: formPayload.isOngoingNeed,
      });

      toast.success(
        'A Clearco Founder Success Representative will be in touch shortly.',
        {
          summary: 'Inquiry Submitted!',
        },
      );

      onSubmitSuccess?.();
    }
  };

  // ============================================================================
  // Lifecycle Hooks
  // ============================================================================
  onMounted(() => {
    // Store original data for change detection
    // This captures the initial values (either from latestInquiry or defaults)
    originalInquiryData.value = {
      capitalAmount: values.capitalAmount,
      fundingType: values.fundingType,
      recurringNeeds: { ...values.recurringNeeds },
      oneTimeNeedDate: values.oneTimeNeedDate,
      capitalUsage: [...(values.capitalUsage || [])],
    };
  });

  // ============================================================================
  // Return Public API
  // ============================================================================
  return {
    // Form fields
    capitalAmount,
    fundingType,
    fundingTypeError,
    recurringNeeds,
    oneTimeNeedDate,
    capitalUsage,

    // Computed properties
    formattedCapitalAmount,
    capitalUsageOptions,
    defaultDates,
    isFormValid,
    hasFormChanged,

    // Methods
    formatDateForDisplay,
    submit,
  };
}
